import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Plus,
  Flag,
  Calendar,
  CheckCircle2,
  Circle,
  Clock,
  MoreVertical,
  Edit2,
  Trash2,
  Link,
  AlertTriangle,
  Target,
  TrendingUp,
  ChevronRight,
  ListTodo,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { Milestone, Task, Project } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';
import { QUERY_KEYS } from '@/services/queryClient';
import { taskService, projectService } from '@/services/api';

dayjs.extend(isBetween);

// Types
interface MilestoneWithProgress extends Milestone {
  progress: number;
  totalTasks: number;
  completedTasks: number;
  status: 'completed' | 'on-track' | 'at-risk' | 'overdue' | 'upcoming';
  daysRemaining: number;
}

interface MilestonesManagementProps {
  project: Project;
  tasks: Task[];
  onUpdate?: (milestones: Milestone[]) => void;
}

// Milestone status colors
const statusColors: Record<string, { bg: string; text: string; border: string }> = {
  completed: { bg: '#dcfce7', text: '#166534', border: '#22c55e' },
  'on-track': { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
  'at-risk': { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  overdue: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' },
  upcoming: { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' },
};

const MilestonesManagement: React.FC<MilestonesManagementProps> = ({
  project,
  tasks,
  onUpdate,
}) => {
  const queryClient = useQueryClient();
  
  // State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const [selectedMilestone, setSelectedMilestone] = useState<MilestoneWithProgress | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuMilestone, setMenuMilestone] = useState<MilestoneWithProgress | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dueDate: dayjs().add(1, 'month') as Dayjs | null,
    taskIds: [] as string[],
  });

  // Calculate milestone progress and status (derive taskIds from tasks when not present, e.g. after API refetch)
  const milestonesWithProgress = useMemo((): MilestoneWithProgress[] => {
    return (project.milestones || []).map((milestone) => {
      const taskIdsForMilestone =
        milestone.taskIds?.length > 0
          ? milestone.taskIds
          : tasks.filter((t) => (t as Task & { milestoneId?: string }).milestoneId === milestone.id).map((t) => t.id);
      const milestoneTasks = tasks.filter((t) => taskIdsForMilestone.includes(t.id));
      const completedTasks = milestoneTasks.filter((t) => t.status === 'completed').length;
      const totalTasks = milestoneTasks.length;
      const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      
      const dueDate = dayjs(milestone.dueDate);
      const today = dayjs();
      const daysRemaining = dueDate.diff(today, 'day');
      
      let status: MilestoneWithProgress['status'];
      if (milestone.isCompleted) {
        status = 'completed';
      } else if (daysRemaining < 0) {
        status = 'overdue';
      } else if (daysRemaining <= 7 && progress < 80) {
        status = 'at-risk';
      } else if (daysRemaining > 14) {
        status = 'upcoming';
      } else {
        status = 'on-track';
      }
      
      return {
        ...milestone,
        taskIds: taskIdsForMilestone,
        progress,
        totalTasks,
        completedTasks,
        status,
        daysRemaining,
      };
    }).sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)));
  }, [project.milestones, tasks]);

  // Stats
  const stats = useMemo(() => {
    const total = milestonesWithProgress.length;
    const completed = milestonesWithProgress.filter((m) => m.status === 'completed').length;
    const atRisk = milestonesWithProgress.filter((m) => m.status === 'at-risk').length;
    const overdue = milestonesWithProgress.filter((m) => m.status === 'overdue').length;
    const upcoming = milestonesWithProgress.filter((m) => m.status === 'upcoming' || m.status === 'on-track').length;
    
    return { total, completed, atRisk, overdue, upcoming };
  }, [milestonesWithProgress]);

  // Handlers
  const handleOpenDialog = (milestone?: Milestone) => {
    if (milestone) {
      setEditingMilestone(milestone);
      setFormData({
        name: milestone.name,
        description: milestone.description || '',
        dueDate: dayjs(milestone.dueDate),
        taskIds: milestone.taskIds || [],
      });
    } else {
      setEditingMilestone(null);
      setFormData({
        name: '',
        description: '',
        dueDate: dayjs().add(1, 'month'),
        taskIds: [],
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMilestone(null);
  };

  const handleSave = async () => {
    const dueDateIso = formData.dueDate?.toISOString() || dayjs().add(1, 'month').toISOString();
    const payload = {
      name: formData.name,
      description: formData.description || undefined,
      dueDate: dueDateIso,
    };

    const previousTaskIds = editingMilestone?.taskIds || [];
    const currentTaskIds = formData.taskIds;
    const removedTaskIds = previousTaskIds.filter((id) => !currentTaskIds.includes(id));
    const addedTaskIds = currentTaskIds.filter((id) => !previousTaskIds.includes(id));

    let milestoneId: string;
    if (editingMilestone) {
      await projectService.updateMilestone(project.id, editingMilestone.id, payload);
      milestoneId = editingMilestone.id;
    } else {
      const created = await projectService.addMilestone(project.id, payload);
      milestoneId = created.id;
    }

    for (const taskId of addedTaskIds) {
      try {
        await taskService.updateTask(taskId, { milestoneId });
      } catch (error) {
        console.warn(`Failed to update milestoneId for task ${taskId}:`, error);
      }
    }
    for (const taskId of removedTaskIds) {
      try {
        await taskService.updateTask(taskId, { milestoneId: undefined });
      } catch (error) {
        console.warn(`Failed to clear milestoneId for task ${taskId}:`, error);
      }
    }

    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(project.id) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTasks(project.id) });
    handleCloseDialog();
  };

  const handleToggleComplete = async (milestone: MilestoneWithProgress) => {
    const isCompleted = !milestone.isCompleted;
    await projectService.updateMilestone(project.id, milestone.id, {
      isCompleted,
      completedAt: isCompleted ? new Date().toISOString() : undefined,
    });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(project.id) });
  };

  const handleDelete = async (milestoneId: string) => {
    const milestoneToDelete = project.milestones.find((m) => m.id === milestoneId);
    if (milestoneToDelete?.taskIds) {
      for (const taskId of milestoneToDelete.taskIds) {
        try {
          await taskService.updateTask(taskId, { milestoneId: undefined });
        } catch (error) {
          console.warn(`Failed to clear milestoneId for task ${taskId}:`, error);
        }
      }
    }
    await projectService.deleteMilestone(project.id, milestoneId);
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(project.id) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTasks(project.id) });
    setMenuAnchor(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, milestone: MilestoneWithProgress) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuMilestone(milestone);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuMilestone(null);
  };

  const handleTaskToggle = (taskId: string) => {
    setFormData((prev) => ({
      ...prev,
      taskIds: prev.taskIds.includes(taskId)
        ? prev.taskIds.filter((id) => id !== taskId)
        : [...prev.taskIds, taskId],
    }));
  };

  // Get available tasks (not assigned to other milestones or in current milestone)
  const availableTasks = useMemo(() => {
    const assignedTaskIds = new Set(
      project.milestones
        ?.filter((m) => m.id !== editingMilestone?.id)
        .flatMap((m) => m.taskIds) || []
    );
    return tasks.filter((t) => !assignedTaskIds.has(t.id) || formData.taskIds.includes(t.id));
  }, [tasks, project.milestones, editingMilestone, formData.taskIds]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Project Milestones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track key deliverables and project phases
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => handleOpenDialog()}
          size="small"
        >
          Add Milestone
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc' }}>
            <Typography variant="h4" fontWeight={700} color="primary.main">
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Milestones
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: statusColors.completed.bg }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: statusColors.completed.text }}>
              {stats.completed}
            </Typography>
            <Typography variant="body2" sx={{ color: statusColors.completed.text }}>
              Completed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: statusColors['at-risk'].bg }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: statusColors['at-risk'].text }}>
              {stats.atRisk}
            </Typography>
            <Typography variant="body2" sx={{ color: statusColors['at-risk'].text }}>
              At Risk
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: statusColors.overdue.bg }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: statusColors.overdue.text }}>
              {stats.overdue}
            </Typography>
            <Typography variant="body2" sx={{ color: statusColors.overdue.text }}>
              Overdue
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Timeline View */}
      {milestonesWithProgress.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <Flag size={48} color="#9ca3af" style={{ marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No milestones yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add milestones to track key project deliverables
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Plus size={16} />}
            onClick={() => handleOpenDialog()}
          >
            Add First Milestone
          </Button>
        </Card>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {/* Timeline line */}
          <Box
            sx={{
              position: 'absolute',
              left: 24,
              top: 0,
              bottom: 0,
              width: 2,
              bgcolor: '#e5e7eb',
              zIndex: 0,
            }}
          />

          {/* Milestone cards */}
          {milestonesWithProgress.map((milestone, index) => (
            <Card
              key={milestone.id}
              sx={{
                mb: 2,
                ml: 5,
                position: 'relative',
                borderLeft: `4px solid ${statusColors[milestone.status].border}`,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateX(4px)',
                },
              }}
              onClick={() => setSelectedMilestone(milestone)}
            >
              {/* Timeline dot */}
              <Box
                sx={{
                  position: 'absolute',
                  left: -33,
                  top: 20,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: statusColors[milestone.status].border,
                  border: '3px solid white',
                  boxShadow: 1,
                  zIndex: 1,
                }}
              />

              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {milestone.isCompleted ? (
                        <CheckCircle2 size={20} color={statusColors.completed.border} />
                      ) : (
                        <Flag size={20} color={statusColors[milestone.status].border} />
                      )}
                      <Typography variant="subtitle1" fontWeight={600}>
                        {milestone.name}
                      </Typography>
                      <Chip
                        label={milestone.status.replace('-', ' ')}
                        size="small"
                        sx={{
                          bgcolor: statusColors[milestone.status].bg,
                          color: statusColors[milestone.status].text,
                          textTransform: 'capitalize',
                          fontWeight: 500,
                        }}
                      />
                    </Box>

                    {milestone.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {milestone.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Calendar size={14} color="#6b7280" />
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(milestone.dueDate).format('MMM D, YYYY')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ListTodo size={14} color="#6b7280" />
                        <Typography variant="body2" color="text.secondary">
                          {milestone.completedTasks}/{milestone.totalTasks} tasks
                        </Typography>
                      </Box>
                      {!milestone.isCompleted && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Clock size={14} color="#6b7280" />
                          <Typography variant="body2" color="text.secondary">
                            {milestone.daysRemaining > 0
                              ? `${milestone.daysRemaining} days left`
                              : `${Math.abs(milestone.daysRemaining)} days overdue`}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Progress bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={milestone.progress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#f3f4f6',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: statusColors[milestone.status].border,
                              borderRadius: 4,
                            },
                          }}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 45 }}>
                        {formatPercent(milestone.progress)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={milestone.isCompleted ? 'Mark incomplete' : 'Mark complete'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleComplete(milestone);
                        }}
                      >
                        {milestone.isCompleted ? (
                          <CheckCircle2 size={20} color="#22c55e" />
                        ) : (
                          <Circle size={20} color="#9ca3af" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, milestone)}
                    >
                      <MoreVertical size={18} />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuMilestone) {
              handleOpenDialog(menuMilestone);
            }
            handleMenuClose();
          }}
        >
          <Edit2 size={16} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuMilestone) {
              handleDelete(menuMilestone.id);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMilestone ? 'Edit Milestone' : 'Add Milestone'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Milestone Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Phase 1 Complete, MVP Launch"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Describe what this milestone represents..."
            />

            <DatePicker
              label="Due Date"
              value={formData.dueDate}
              onChange={(date) => setFormData({ ...formData, dueDate: date })}
              maxDate={project.endDate ? dayjs(project.endDate) : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: project.endDate ? `Cannot exceed project end date: ${dayjs(project.endDate).format('MMM D, YYYY')}` : undefined,
                },
              }}
            />

            {/* Task Assignment */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Associated Tasks ({formData.taskIds.length} selected)
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto' }}>
                <List dense>
                  {availableTasks.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="No tasks available"
                        secondary="All tasks are assigned to other milestones"
                      />
                    </ListItem>
                  ) : (
                    availableTasks.map((task) => (
                      <ListItemButton
                        key={task.id}
                        onClick={() => handleTaskToggle(task.id)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={formData.taskIds.includes(task.id)}
                            tabIndex={-1}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={task.title}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip
                                label={task.status.replace('_', ' ')}
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              {task.dueDate && (
                                <Typography variant="caption" color="text.secondary">
                                  Due: {dayjs(task.dueDate).format('MMM D')}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    ))
                  )}
                </List>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name.trim()}
          >
            {editingMilestone ? 'Save Changes' : 'Add Milestone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Milestone Detail Drawer/Dialog - could be expanded */}
      <Dialog
        open={Boolean(selectedMilestone)}
        onClose={() => setSelectedMilestone(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedMilestone && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flag size={20} color={statusColors[selectedMilestone.status].border} />
                {selectedMilestone.name}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Chip
                  label={selectedMilestone.status.replace('-', ' ')}
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: statusColors[selectedMilestone.status].bg,
                    color: statusColors[selectedMilestone.status].text,
                    textTransform: 'capitalize',
                  }}
                />

                {selectedMilestone.description && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedMilestone.description}
                  </Typography>
                )}

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Due Date</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {dayjs(selectedMilestone.dueDate).format('MMMM D, YYYY')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Progress</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatPercent(selectedMilestone.progress)} ({selectedMilestone.completedTasks}/{selectedMilestone.totalTasks} tasks)
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={selectedMilestone.progress}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: '#f3f4f6',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: statusColors[selectedMilestone.status].border,
                    },
                  }}
                />

                <Divider />

                <Typography variant="subtitle2">Associated Tasks</Typography>
                <List dense>
                  {tasks
                    .filter((t) => selectedMilestone.taskIds.includes(t.id))
                    .map((task) => (
                      <ListItem key={task.id}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {task.status === 'completed' ? (
                            <CheckCircle2 size={16} color="#22c55e" />
                          ) : (
                            <Circle size={16} color="#9ca3af" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={task.title}
                          secondary={dayjs(task.dueDate).format('MMM D')}
                        />
                      </ListItem>
                    ))}
                </List>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedMilestone(null)}>Close</Button>
              <Button
                variant="outlined"
                onClick={() => {
                  handleOpenDialog(selectedMilestone);
                  setSelectedMilestone(null);
                }}
              >
                Edit Milestone
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MilestonesManagement;
